import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from '@mui/material';
import { Button, Dialog } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useCrossCheckUtils } from '../utils';
import CardProperty from './CascoCardPropertyGrid';
import _ from 'lodash';
import {
  ComparisonContainer,
  CascoCrossCheckContent,
  CrossCheckTitleContainer,
  CrossCheckTitle,
  CascoNoQuotationWrapper,
  ComparisonCard,
  CascoComparisonTitle
} from '../FlowCheckStyles';

const CascoCrossCheckComparatorModal = ({ onCloseModal, existingQuotations, newQuotations }) => {
  const { t } = useTranslation();
  const { getCrossCheckComparison } = useCrossCheckUtils();

  const comparisons = useMemo(() => {
    // Get a collection of all { productId, insurerId, insurerName, period } pairs in existing and new quotations
    if (!existingQuotations || !newQuotations) return;
    const insurerPeriodPairs = [...existingQuotations, ...newQuotations]
      .map(({ productId, insurerId, insurerName, period }) => ({ productId, insurerId, insurerName, period }))
      .sort((a, b) => {
        if (a.insurerName < b.insurerName) return -1;
        if (a.insurerName > b.insurerName) return 1;
        return a.period - b.period;
      });
    // Get a distinct collection of { productId, insurerId, insurerName, period }
    const insurerPeriodSet = _.uniqWith(insurerPeriodPairs, _.isEqual);
    const result = insurerPeriodSet.map(({ productId, insurerId, period }) => {
      const existingQuotation = existingQuotations.find(x => x.productId === productId && x.insurerId === insurerId && x.period === period);
      const newQuotation = newQuotations.find(x => x.productId === productId && x.insurerId === insurerId && x.period === period);
      return [
        getCrossCheckComparison(existingQuotation, newQuotation, 'error'),
        getCrossCheckComparison(newQuotation, existingQuotation, 'success')
      ];
    });
    return result;
  }, [existingQuotations, newQuotations, getCrossCheckComparison]);

  return (
    <Dialog
      id='casco-cross-check-dialog'
      open
      fullWidth
      maxWidth={'md'}
      onClose={onCloseModal}
      closeButtonProps={{ sx: { display: 'none' } }}
      sx={{ '& .MuiDialogTitle-root': { p: 0 } }}
      title={
        <CrossCheckTitleContainer>
          <CrossCheckTitle>{t('Settings.InsuranceIntegrationChecks.CascoCheck.QuotationsThen')}</CrossCheckTitle>
          <CrossCheckTitle>{t('Settings.InsuranceIntegrationChecks.CascoCheck.QuotationsNow')}</CrossCheckTitle>
        </CrossCheckTitleContainer>
      }
      content={
        <CascoCrossCheckContent container mt={2}>
          {comparisons &&
            comparisons.length > 0 &&
            comparisons.map((comparison, idxComparison) => (
              <>
                {comparison.map(({ color, data }, idxQuotation) => (
                  <ComparisonContainer item container key={`${idxComparison}_${idxQuotation}`} xs={6} existing={idxQuotation === 0}>
                    <ComparisonCard item container>
                      {!data && (
                        <CascoNoQuotationWrapper color={color}>
                          {t('Settings.InsuranceIntegrationChecks.NoQuotationRetrieved')}
                        </CascoNoQuotationWrapper>
                      )}
                      {data && (
                        <>
                          <CascoComparisonTitle item container xs={12} changed={data.insurerName.changed} changedColor={color}>
                            {data.insurerName.value || 'N/A'}
                          </CascoComparisonTitle>
                          <CardProperty
                            data={data.conventionName}
                            label={t('Settings.InsuranceIntegrationChecks.CascoCheck.ConventionName')}
                            color={color}
                          />
                          <CardProperty
                            data={data.period}
                            label={t('Settings.InsuranceIntegrationChecks.CascoCheck.Period')}
                            color={color}
                          />
                          <CardProperty
                            data={data.insuranceAmount}
                            label={t('Settings.InsuranceIntegrationChecks.CascoCheck.Amount')}
                            color={color}
                          />
                          <CardProperty
                            data={data.totalInsuranceAmount}
                            label={t('Settings.InsuranceIntegrationChecks.CascoCheck.TotalAmount')}
                            color={color}
                          />
                          <CardProperty
                            data={data.currencyCode}
                            label={t('Settings.InsuranceIntegrationChecks.CascoCheck.Currency')}
                            color={color}
                          />
                        </>
                      )}
                    </ComparisonCard>
                  </ComparisonContainer>
                ))}
              </>
            ))}
          {!comparisons && (
            <Grid item container xs={12}>
              <div>{t('Settings.InsuranceIntegrationChecks.NoQuotationsRetrieved')}</div>
            </Grid>
          )}
        </CascoCrossCheckContent>
      }
      actions={
        <Box mr={1} mb={1}>
          <Button color={'primary'} onClick={onCloseModal}>
            {t('General.Close')}
          </Button>
        </Box>
      }
    />
  );
};

CascoCrossCheckComparatorModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  existingQuotations: PropTypes.array,
  newQuotations: PropTypes.array
};

export default CascoCrossCheckComparatorModal;
